import { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import CommonConfig from '../../../utils/constant';
import Loader from "../../Shared/Loader/Loader";
import APIConstant from "../../../utils/pathConstants";
import api from "../../../utils/apiClient";
import Toast from '../../Shared/Toast/Toast';
import moment from 'moment';
import { InputText } from 'primereact/inputtext';
import { FloatLabel } from 'primereact/floatlabel';
import { useCatchResponse } from '../../../utils/useCatchResponse';
import { useNavigate } from 'react-router-dom';
import { AutoComplete } from 'primereact/autocomplete';

const ClassConfig = (props) => {
	const { catchResponse } = useCatchResponse();
	const navigate = useNavigate();
	const studentListRef = useRef(null);
	const [securityUserID, setSecurityUserID] = useState("");
	const [classConfigurationId, setClassConfigurationId] = useState('');
	const [classDuration, setClassDuration] = useState({ teachingRange: [], breaks: [{ breakFrom: null, breakTo: null }] });
	const [classDurationErr, setClassDurationErr] = useState({ teachingRange: false, breakFrom: false, breakTo: false });
	const [classDurationErrText, setClassDurationErrText] = useState({ teachingRange: '', breakFrom: '', breakTo: '' });
	const [selectedTeacher, setSelectedTeacher] = useState(null);
	const [classTeacherErr, setClassTeacherErr] = useState(false);
	const [classTeacherErrText, setClassTeacherErrText] = useState('');
	const [teachersList, setTeachersList] = useState([]);
	const [subjectList, setSubjectList] = useState([]);
	const [duration, setDuration] = useState(0);
	const [interval, setInterval] = useState(0);
	const [selectedAttendanceType, setSelectedAttendanceType] = useState([]);
	const [attendanceTypeList, setAttendanceTypeList] = useState([{
		label: 'Class Teacher', value: 'ClassTeacher'
	},
	{
		label: 'Subject Teacher', value: 'SubjectTeacher'
	},
	{
		label: 'First Lecture', value: 'FirstLecture'
	}
	]);

	const handleTeacherChange = (rowData, teacherIndex, newValue) => {
		const updatedSubjects = subjectList.map((subject) => {
			if (subject?.SubjectId === rowData?.SubjectId) {
				const updatedTeachersList = [...subject?.TeachersList];
				updatedTeachersList[teacherIndex] = {
					...updatedTeachersList[teacherIndex],
					id: newValue,
				};
				return { ...subject, TeachersList: updatedTeachersList };
			}
			return subject;
		});
		setSubjectList(updatedSubjects);
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				Loader.show();
				const loginData = CommonConfig.loginData();
				setSecurityUserID(loginData?._id);
				await GetSubjectList(loginData?._id);
				await GetTeachersList(loginData?._id);
				await GetClassConfigurationDetail(loginData?._id);
				Loader.hide();
			} catch (error) {
				Loader.hide();
				console.error('Error in useEffect:', error);
			} finally {
				Loader.hide();
			}
		};
		fetchData();
	}, []);

	useEffect(() => {
		GetClassConfigurationDetail();
	}, [props?.location?.state?.configId, teachersList]);

	const GetTeachersList = async (id) => {
		try {
			const data = { UserID: id };
			await api.post(APIConstant.path.getUser, data).then(async response => {
				if (response.success) {
					const dataList = response.data
						.filter((x) => x.UserType === 'Teacher' && x?.Status === 'Active')
						.map((y) => ({
							...y,
							label: y.Name,
							value: y._id
						}));
					setTeachersList(dataList);
					Loader.hide();
				}
			}).catch(err => {
				Loader.hide();
				console.log(err);
				catchResponse(err);
			});
		} catch (err) {
			console.log(err);
			catchResponse(err);
		}
	};

	const GetSubjectList = async (id) => {
		try {
			let data = {
				ClassId: props?.location?.state?.configId,
				UserID: id
			}
			await api.post(APIConstant.path.getSubjectByClassId, data).then(async response => {
				let res = response;
				if (res.success) {
					setSubjectList(res.data);
					Loader.hide();
				}
			}).catch(err => {
				Loader.hide();
				console.log(err);
				catchResponse(err);
			});
		} catch (err) {
			console.log(err);
			catchResponse(err);
		}
	}

	const GetClassConfigurationDetail = async () => {
		try {
			let data = {
				ClassId: props?.location?.state?.configId,
			}
			Loader.show();
			await api.post(APIConstant.path.getClassConfigurationData, data).then(async response => {
				let res = response;
				if (res.success) {
					let resData = res.data ? res?.data[0] : {};
					if (resData) {
						setClassConfigurationId(resData?._id);
						const breakFromArray = resData?.BreakFrom ? resData?.BreakFrom.split(",").map(date => new Date(date)) : [];
						const breakToArray = resData?.BreakTo ? resData?.BreakTo.split(",").map(date => new Date(date)) : [];
						const breaks = breakFromArray?.map((breakFrom, index) => ({
							breakFrom,
							breakTo: breakToArray[index] || null,
						}));
						setClassDuration({ ...classDuration, teachingRange: [resData?.TeachingFrom ? new Date(resData?.TeachingFrom) : null, resData?.TeachingTo ? new Date(resData?.TeachingTo) : null], breaks: breaks });
						setSelectedTeacher(resData?.ClassTeacherId || null);
						setDuration(resData?.Duration);
						setInterval(resData?.Interval);
						setSelectedAttendanceType(resData?.AttendanceType);
					}
					Loader.hide();
				}
			}).catch(err => {
				Loader.hide();
				console.log(err);
				catchResponse(err);
			});
		} catch (err) {
			Loader.hide();
			console.log(err);
			catchResponse(err);
		}
	}

	const validate = () => {
		let formIsValid = true;
		const [fromTime, toTime] = classDuration?.teachingRange || [];
		if (CommonConfig.isEmpty(fromTime)) {
			formIsValid = false;
			setClassDurationErr((prev) => ({ ...prev, teachingRange: true }));
			setClassDurationErrText((prev) => ({ ...prev, teachingRange: "Teaching From Time is required" }));
		} else if (classDurationErr?.teachingFrom === true) {
			formIsValid = false;
		} else if (CommonConfig.isEmpty(toTime)) {
			formIsValid = false;
			setClassDurationErr((prev) => ({ ...prev, teachingRange: true }));
			setClassDurationErrText((prev) => ({ ...prev, teachingRange: "Teaching To Time is required" }));
		} else if (classDurationErr?.teachingTo === true) {
			formIsValid = false;
		} else {
			setClassDurationErr((prev) => ({ ...prev, teachingRange: false }));
			setClassDurationErrText((prev) => ({ ...prev, teachingRange: "" }));
		}
		if (CommonConfig.isEmpty(selectedTeacher)) {
			formIsValid = false;
			setClassTeacherErr(true);
			setClassTeacherErrText("Teacher is required");
		} else {
			setClassTeacherErr(false);
			setClassTeacherErrText("");
		}
		const [firstBreak] = classDuration?.breaks || [];
		if (firstBreak) {
			if (!firstBreak.breakFrom) {
				formIsValid = false;
				setClassDurationErr(prev => ({ ...prev, breakFrom: true }));
				setClassDurationErrText(prev => ({ ...prev, breakFrom: "Break From time is required" }));
			} else {
				setClassDurationErr(prev => ({ ...prev, breakFrom: false }));
				setClassDurationErrText(prev => ({ ...prev, breakFrom: "" }));
			}

			if (!firstBreak.breakTo) {
				formIsValid = false;
				setClassDurationErr(prev => ({ ...prev, breakTo: true }));
				setClassDurationErrText(prev => ({ ...prev, breakTo: "Break To time is required" }));
			} else {
				setClassDurationErr(prev => ({ ...prev, breakTo: false }));
				setClassDurationErrText(prev => ({ ...prev, breakTo: "" }));
			}
		}
		return formIsValid;
	};

	const AddClassConfiguration = async (e) => {
		if (validate(e)) {
			try {
				let breakFrom = classDuration?.breaks?.map((breakItem) => moment(breakItem?.breakFrom).format("YYYY-MM-DDTHH:mm:ss"))
					.join(",");
				let breakTo = classDuration?.breaks?.map((breakItem) => moment(breakItem?.breakTo).format("YYYY-MM-DDTHH:mm:ss"))
					.join(",");
				const [fromTime, toTime] = classDuration?.teachingRange || [];
				let data = {
					id: classConfigurationId,
					ClassId: props?.location?.state?.configId,
					Duration: duration,
					Interval: interval,
					TeachingFrom: moment(fromTime),
					TeachingTo: moment(toTime),
					BreakFrom: breakFrom,
					BreakTo: breakTo,
					ClassTeacherId: selectedTeacher ?? '',
					AttendanceType: selectedAttendanceType,
					CreatedBy: securityUserID,
					UpdatedBy: classConfigurationId ? securityUserID : '',
				};

				Loader.show();
				await api.post(APIConstant.path.AddUpdateClassConfiguration, data).then(async response => {
					let res = response;
					if (res.success) {
						await EditSubjectMaster();
						if (studentListRef?.current) {
							studentListRef?.current?.saveStudentRollNo();
							// studentListRef?.current?.clear();
						}
						Toast.success({ message: res.message });
						Loader.hide();
					} else {
						Toast.error({ message: res.message });
						Loader.hide();
					}
				})
					.catch(() => {
						Loader.hide();
					});
			} catch (err) {
				console.log(err);
				Loader.hide();
				catchResponse(err);
			}
		}
	};

	const EditSubjectMaster = async () => {
		try {
			Loader.show();
			subjectList.map(async x => {
				let data = {
					id: x._id,
					Teachers: x?.TeachersList ? x?.TeachersList.map(x => x?.id).length > 1 ? x?.TeachersList.map(x => x?.id).join(',') : x?.TeachersList.map(x => x?.id)[0] : '',
				};
				await api
					.post(APIConstant.path.AddUpdateSubjectMaster, data)
					.then((response) => {
						let res = response;
						if (res.success) {
							Loader.hide();
						} else {
							Loader.hide();
						}
					})
					.catch((err) => {
						console.log(err);
						Loader.hide();
					});
			})
		} catch (err) {
			console.log(err);
			Loader.hide();
			catchResponse(err);
		}

	};

	const actionBodyTemplateSrNo = (data, props) => {
		return props.rowIndex + 1;
	};

	const handleTeachingRangeChange = (range) => {
		const [from, to] = range || [];

		if (from && to && from >= to) {
			setClassDurationErr((prev) => ({ ...prev, teachingRange: true }));
			setClassDurationErrText((prev) => ({
				...prev,
				teachingRange: "'From' time must be before 'To' time"
			}));
		} else {
			setClassDurationErr((prev) => ({ ...prev, teachingRange: false }));
			setClassDurationErrText((prev) => ({ ...prev, teachingRange: '' }));
		}

		setClassDuration((prev) => ({
			...prev,
			teachingRange: range
		}));
	};

	const handleBreakChange = (index, key, value) => {
		const updatedBreaks = [...classDuration.breaks];
		updatedBreaks[index][key] = value;
		setClassDuration({ ...classDuration, breaks: updatedBreaks });
	};

	const addBreak = () => {
		setClassDuration({
			...classDuration,
			breaks: [...classDuration.breaks, { breakFrom: null, breakTo: null }]
		});
	};

	const removeBreak = (index) => {
		const updatedBreaks = classDuration.breaks.filter((_, i) => i !== index);
		setClassDuration({ ...classDuration, breaks: updatedBreaks });
	};

	return <div>
		<div className="wayment-owner-dashboard" style={{ paddingInline: '0.5rem' }}>
			<div className='p-grid'>
				<div className="p-col-12 p-px-0">
					<div className="p-d-flex p-ai-baseline">
						<i
							className="pi pi-arrow-left"
							style={{ cursor: 'pointer', marginRight: '1rem' }}
							onClick={() => navigate('/class-configuration')}
							title="Back"
						></i>
						<h3>Class Configuration</h3>
					</div>
					<hr className='p-m-0' />
					<div className="p-field p-col-4">
						<label htmlFor="teachingTimeRange">Teaching Time Range</label>
						<div className="p-grid">
							<div className="p-col-6">
								<div className='custom-inputs'>
									<label>From</label>
									<Calendar
										value={classDuration.teachingRange?.[0]}
										onChange={(e) =>
											handleTeachingRangeChange([e.value, classDuration.teachingRange?.[1] || null])
										}
										showTime
										hourFormat="12"
										className='e-assign-btn p-w-100 p-mt-1 p-pl-0'
										timeOnly
										showButtonBar
									/>
								</div>
							</div>

							<div className="p-col-6">
								<div className='custom-inputs'>
									<label>To</label>
									<Calendar
										value={classDuration.teachingRange?.[1]}
										onChange={(e) =>
											handleTeachingRangeChange([classDuration.teachingRange?.[0] || null, e.value])
										}
										showTime
										hourFormat="12"
										timeOnly
										className='e-assign-btn p-w-100 p-mt-1 p-pl-0'
										showButtonBar
									/>
								</div>
							</div>
						</div>
						{classDurationErr?.teachingRange && (
							<span className="error-msg" style={{ color: 'red' }}>
								{classDurationErrText?.teachingRange}
							</span>
						)}
					</div>
					<div className='p-mt-1'>
						<div className='p-d-flex p-ai-center p-jc-between p-mb-1'>
							<h4 className='p-m-0'>Breaks</h4>
							<Button icon="pi pi-plus" className="e-sction-btn p-m-0" onClick={addBreak} />
						</div>
						<hr className='p-m-0' />
						{classDuration.breaks.map((breakItem, index) => (
							<div className="p-grid" key={index}>
								<div className='p-col-12 p-md-6'>
									<div className='custom-inputs'>
										<p className="p-m-0">Break From</p>
										<p className="p-m-0">
											<Calendar
												className='p-w-100 p-mt-1'
												value={breakItem.breakFrom}
												onChange={(e) => handleBreakChange(index, 'breakFrom', e.value)}
												hourFormat="12"
												timeOnly
												dateFormat="dd/mm/yy"
											/>
										</p>
									</div>
								</div>
								<div className='p-col-12 p-md-6'>
									<div className='custom-inputs'>
										<p className="p-m-0">Break To</p>
										<p className="p-m-0">
											<Calendar
												className='p-w-100 p-mt-1'
												value={breakItem.breakTo}
												onChange={(e) => handleBreakChange(index, 'breakTo', e.value)}
												hourFormat="12"
												timeOnly
												dateFormat="dd/mm/yy"
											/>
										</p>
									</div>
								</div>
								<div className="p-d-flex p-ai-center p-jc-end p-w-100">
									<Button
										label="Remove"
										className="p-button-danger p-m-0 p-button-text"
										onClick={() => removeBreak(index)}
									/>
								</div>
							</div>
						))}
						<span className="error-msg" style={{ color: 'red' }}>
							{classDurationErr?.breakFrom ? classDurationErrText?.breakFrom : null}
						</span>
						<span className="error-msg" style={{ color: 'red' }}>
							{classDurationErr?.breakTo ? classDurationErrText?.breakTo : null}
						</span>
					</div>
					<div className="card-header-left p-grid">
						<div className='p-col-12 p-md-4'>
							<div className='custom-inputs'>
								<label htmlFor="duration">Duration (minute)</label>
								<FloatLabel>
									<InputText
										className="p-mt-1 p-w-100 e-assign-btn"
										id="duration"
										type="number"
										placeholder="Duration"
										value={duration}
										autoFocus={true}
										onChange={(e) => setDuration(e.target.value)}
									/>
								</FloatLabel>
							</div>
						</div>
						<div className='p-col-12 p-md-4'>
							<div className='custom-inputs'>
								<label htmlFor="interval">Interval (minute)</label>
								<FloatLabel>
									<InputText
										className="p-mt-1 p-w-100 e-assign-btn"
										id="interval"
										type="number"
										placeholder="Interval"
										value={interval}
										onChange={(e) => setInterval(e.target.value)}
									/>
								</FloatLabel>
							</div>
						</div>
						<div className='p-col-12 p-md-4'>
							<div className='custom-inputs'>
								<label>Class Teacher</label>
								<Dropdown
									className='p-mt-1 p-w-100'
									value={selectedTeacher}
									options={teachersList}
									placeholder="Select Teacher"
									filter showClear
									onChange={(e) => setSelectedTeacher(e.value)}
								/>
								<span className="error-msg" style={{ color: 'red' }}>
									{classTeacherErr ? classTeacherErrText : null}
								</span>
							</div>
						</div>
						<div className='p-col-12 p-md-4'>
							<div className='custom-inputs'>
								<label>Attendance Type</label>
								<Dropdown className='p-mt-1 p-w-100' value={selectedAttendanceType} options={attendanceTypeList} onChange={(e) => setSelectedAttendanceType(e.value)} placeholder="Select Attendance Type" filter showClear />
							</div>
						</div>
					</div>
				</div>

				<div className="p-col-12 p-p-3 p-mt-4">
					<div>
						<h4 className='p-m-0 p-pb-2'>Subject Teacher</h4>
					</div>
					<hr className='p-m-0' />
					<DataTable value={subjectList} paginator rows={10} className='custom-table'
						scrollHeight='calc(100vh - 320px)'
						size='small'>
						<Column body={actionBodyTemplateSrNo} header="Sr No." />
						<Column field="SubjectName" header="Subject Name" />
						<Column field="SubjectId" header="Subject Code" />
						<Column field="primaryTeacher" header="Primary Teacher" body={(rowData) => (
							<Dropdown className='p-w-100' value={rowData?.TeachersList[0]?.id || null} options={rowData?.TeachersList} onChange={(e) => handleTeacherChange(rowData, 0, e.value)} showClear filter display="chip" />
						)} />
						<Column field="secondaryTeacher" header="Secondary Teacher" body={(rowData) => (
							<Dropdown className='p-w-100' value={rowData?.TeachersList[1]?.id || null} options={rowData?.TeachersList} onChange={(e) => handleTeacherChange(rowData, 1, e.value)} showClear filter display="chip" />
						)} />
					</DataTable>
				</div>
			</div>
			<div className="dialog-footer p-d-flex p-ai-center p-jc-end">
				<Button label="Cancel" className="p-m-0 btn-text-action-secondary p-m-1" onClick={() => navigate('/class-configuration')} />
				<Button label="Save" className="p-m-0 btn-text-action-primary" onClick={(e) => AddClassConfiguration(e)} />
			</div>
		</div>
	</div >
}

export default ClassConfig;
